"use client";
/**
 * The face-up comparison card: 236 pixels of "which of these".
 *
 * Every card is the same height, and that is structural rather than remembered:
 * the shell's `auto 1fr auto` regions let the body absorb the difference
 * between a short card and a tall one, so no card leaves a void and no card
 * needs a floor.
 *
 * It stamps its OWN name. This composition is fixed — demonstration frame,
 * title, meta line, muscle line — and none of the three card selections changes
 * any of it, so borrowing the live selection's name would stamp `Load board` on
 * a card carrying a 230-pixel video frame, and `Load board` is the composition
 * declared to have no media at all.
 *
 * Unlike the row, the rail card DOES lead with the frame. A deck is a
 * comparison object a reader scans by picture, and the frame's designed absence
 * is itself the comparison on this pillar: one resolved clip beside two rows
 * that were never resolved is the honest shape of a catalog page whose video
 * costs a second call.
 */
import type { ReactElement } from "react";
import {
  CresultcardIdentity,
  CresultcardOpenHint,
  CRC_TILE_INSET,
  CresultcardDensity,
  CresultcardIdentitySlot,
  CresultcardMediaScale,
  CresultcardTilePadding,
  CresultcardTitleLines,
} from "@/components/ui/Cresultcard";
import {
  C_WORKOUT_RAIL_MODIFIER,
  C_WORKOUT_RAIL_VARIANT,
} from "./cworkoutcard.constants";
import {
  CworkoutcardMetaLine,
  CworkoutcardMuscleLine,
} from "./CworkoutcardMeta";
import { CworkoutcardShell } from "./CworkoutcardShell";
import { CworkoutcardStateCapsule } from "./CworkoutcardStateCapsule";
import { CworkoutcardVideo } from "./CworkoutcardVideo";
import type { CworkoutcardRailProps } from "./cworkoutcard.types";

const CARD_INSET = CRC_TILE_INSET[CresultcardTilePadding.Snug];
const RAIL_TRUNCATION: readonly CresultcardIdentitySlot[] = [
  CresultcardIdentitySlot.Title,
];

export function CworkoutcardRail({
  data,
  openControl,
}: CworkoutcardRailProps): ReactElement {
  return (
    <CworkoutcardShell
      density={CresultcardDensity.Rail}
      head={
        <CworkoutcardVideo
          data={data}
          scale={CresultcardMediaScale.Face}
        />
      }
      modifier={C_WORKOUT_RAIL_MODIFIER}
      openable
      variant={C_WORKOUT_RAIL_VARIANT}
      body={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            minWidth: 0,
            padding: CARD_INSET,
          }}
        >
          <CresultcardIdentity
            openControl={openControl}
            title={data.title}
            titleLines={CresultcardTitleLines.Two}
            truncationOrder={RAIL_TRUNCATION}
          />
          <CworkoutcardMetaLine
            data={data}
          />
        </div>
      }
      foot={
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: CARD_INSET,
            minWidth: 0,
            paddingBlock: CARD_INSET,
            paddingInline: CARD_INSET,
          }}
        >
          <CworkoutcardMuscleLine
            data={data}
          />
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              flex: "0 0 auto",
            }}
          >
            <CworkoutcardStateCapsule
              data={data}
            />
            <CresultcardOpenHint
            />
          </span>
        </div>
      }
    />
  );
}
